import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { generateId } from '@/lib/format';
import { Transaction, TransactionType } from '@/lib/types';
import { formatAUD } from '@/lib/format';
import { ArrowLeft } from 'lucide-react';

export default function AddTransaction() {
  const { data, updateData } = useAuth();
  const navigate = useNavigate();
  const [type, setType] = useState<TransactionType>('expense');
  const [amount, setAmount] = useState('');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [taxDeductible, setTaxDeductible] = useState(false);
  const [superContribution, setSuperContribution] = useState('');
  const [accountId, setAccountId] = useState(data.accounts?.[0]?.id ?? '');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!amount || !category) return;

    const parsedAmount = parseFloat(amount);
    if (isNaN(parsedAmount) || !isFinite(parsedAmount) || parsedAmount < 0) return;

    const parsedSuper = superContribution ? parseFloat(superContribution) : undefined;
    if (parsedSuper !== undefined && (isNaN(parsedSuper) || !isFinite(parsedSuper) || parsedSuper < 0)) return;

    const txn: Transaction = {
      id: generateId(),
      type,
      amount: parsedAmount,
      date,
      category,
      description,
      taxDeductible,
      superContribution: parsedSuper,
      accountId: accountId || undefined,
      createdAt: new Date().toISOString(),
    };

    await updateData(prev => ({
      ...prev,
      transactions: [...prev.transactions, txn],
    }));
    navigate('/transactions');
  };

  return (
    <div className="px-4 pt-6">
      <div className="mb-4 flex items-center gap-2">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="font-display text-xl font-bold">Add Transaction</h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="flex gap-2">
          <Button type="button" variant={type === 'expense' ? 'default' : 'outline'} className="flex-1" onClick={() => setType('expense')}>
            Expense
          </Button>
          <Button type="button" variant={type === 'income' ? 'default' : 'outline'} className="flex-1" onClick={() => setType('income')}>
            Income
          </Button>
        </div>

        <div>
          <Label>Amount (AUD)</Label>
          <Input type="number" step="0.01" min="0" placeholder="0.00" value={amount} onChange={e => setAmount(e.target.value)} className="h-12 text-lg" required />
        </div>

        <div>
          <Label>Date</Label>
          <Input type="date" value={date} onChange={e => setDate(e.target.value)} className="h-12" />
        </div>

        <div>
          <Label>Category</Label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="h-12"><SelectValue placeholder="Select category" /></SelectTrigger>
            <SelectContent>
              {data.categories.map(c => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label>Description (optional)</Label>
          <Input value={description} onChange={e => setDescription(e.target.value)} placeholder="What was this for?" className="h-12" />
        </div>

        <div className="flex items-center justify-between rounded-lg border p-3">
          <Label className="cursor-pointer">Tax deductible</Label>
          <Switch checked={taxDeductible} onCheckedChange={setTaxDeductible} />
        </div>

        {(data.accounts ?? []).length > 0 && (
          <div>
            <Label>Account</Label>
            <Select value={accountId || 'none'} onValueChange={v => setAccountId(v === 'none' ? '' : v)}>
              <SelectTrigger className="h-12"><SelectValue placeholder="Select account" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No account</SelectItem>
                {data.accounts.map(a => (
                  <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {type === 'income' && (
          <div>
            <Label>Super contribution (optional)</Label>
            <Input type="number" step="0.01" min="0" placeholder="0.00" value={superContribution} onChange={e => setSuperContribution(e.target.value)} className="h-12" />
          </div>
        )}

        <Button type="submit" className="h-12 w-full text-base">Save Transaction</Button>
      </form>
    </div>
  );
}
